import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * Performance + accessibilità (pattern riusato da Gala Gloves):
 * - il video NON si scarica al load (preload="none", nessun load() incondizionato);
 * - autoplay solo su desktop, connessione non lenta e senza prefers-reduced-motion;
 * - altrimenti resta il poster e il video parte su tap del pulsante;
 * - caricamento differito quando la sezione entra nel viewport (IntersectionObserver).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [videoToggle] - The play/pause button.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #loaded = false;
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement, videoToggle } = this.refs;

    let saveData = false;
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn) saveData = conn.saveData === true || ['slow-2g', '2g', '3g'].includes(conn.effectiveType);
    } catch (e) {
      /* Network Information API non supportata: ignora */
    }

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.matchMedia?.('(max-width: 749px)').matches;
    this.autoplayAllowed = !reduceMotion && !isMobile && !saveData;

    if (videoToggle) {
      videoToggle.addEventListener('click', this.#toggle);
      videoElement.addEventListener('play', this.#onPlay);
      videoElement.addEventListener('pause', this.#onPause);
    }

    if (this.autoplayAllowed && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this.#load();
            this.#play();
            this.#observer?.disconnect();
          }
        },
        { rootMargin: '200px' }
      );
      this.#observer.observe(this);
    }
    // Se l'autoplay non è consentito (mobile / reduced-motion / data-saver):
    // niente download, resta il poster; il video parte solo al tap del pulsante.
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
  }

  /** Inietta le source e carica il video una sola volta. */
  #load() {
    if (this.#loaded) return;
    const { videoSources, videoElement } = this.refs;
    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }
    videoElement.load();
    this.#loaded = true;
  }

  #play() {
    const result = this.refs.videoElement.play();
    if (result && typeof result.catch === 'function') result.catch(() => this.#sync(false));
  }

  #toggle = () => {
    const video = this.refs.videoElement;
    if (video.paused) {
      this.#load();
      this.#play();
    } else {
      video.pause();
    }
  };

  #onPlay = () => this.#sync(true);
  #onPause = () => this.#sync(false);

  /** Sincronizza icona e stato ARIA del pulsante. */
  #sync(playing) {
    const btn = this.refs.videoToggle;
    if (!btn) return;
    btn.classList.toggle('is-paused', !playing);
    btn.setAttribute('aria-pressed', String(playing));
    btn.setAttribute('aria-label', playing ? 'Metti in pausa il video' : 'Riproduci il video');
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
